import { AccountHubspot } from '@/client/accounts';
import { DateFormats } from '@/system-settings/enums';
import AppChip from '@/ui/chip/AppChip';
import { getHubspotIsActiveStatus } from '@/utils/hubspot/hubspot.helper';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledCardsContainer = styled.div`
  border-radius: var(--semi-medium-border-radius);
  background: var(--beige-main);
  padding: var(--big-padding);
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--default-padding-small);

  & > *:nth-child(-n + 2) {
    grid-column: span 3;
  }

  & > *:nth-child(n + 3) {
    grid-column: span 2;
  }
`;

const StyledCard = styled.div`
  background-color: var(--white-main);
  border-radius: var(--box-border-radius);
  padding: var(--default-padding);
  border: 1px solid var(--gray-light);

  hr {
    height: 1px;
    border: none;
    background-color: var(--gray-moderate);
    margin: var(--semi-medium-padding) 0;
  }
`;

const StyledCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledProductName = styled.span`
  font-size: var(--small-font-size);
  font-weight: 600;
`;

const StyledOnboardingDate = styled.p`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  line-height: 140%;
  margin: 0;
`;

const StyledSectionDescription = styled.p`
  font-size: var(--small-font-size);
  margin: 0 0 var(--default-padding);
`;

type ProductLifecycleCardProps = {
  name: string;
  status: string | undefined;
  onboardingDate: string | null | undefined;
};

const ProductLifecycleCard: React.FC<ProductLifecycleCardProps> = ({
  name,
  status,
  onboardingDate,
}) => {
  const { t } = useTranslation();
  const chipType = getHubspotIsActiveStatus(status) ? 'primary' : 'error';
  const displayStatus = !status
    ? t('generic.inactive')
    : status === 'Free trial'
    ? t('hubspot.productLifecycle.freeTrial')
    : status;
  const formattedOnboardingDate = onboardingDate
    ? moment(onboardingDate).format(DateFormats.DATEONLY_DASHED)
    : '-';

  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledProductName>{name}</StyledProductName>
        <AppChip label={displayStatus} type={chipType} size="s" />
      </StyledCardHeader>
      <hr />
      <StyledOnboardingDate>
        {t('hubspot.productLifecycle.onboardingDate')}:{' '}
        {formattedOnboardingDate}
      </StyledOnboardingDate>
    </StyledCard>
  );
};

type HubspotProductLifecycleProps = {
  hubspot: AccountHubspot | undefined;
};

export const HubspotProductLifecycle: React.FC<
  HubspotProductLifecycleProps
> = ({ hubspot }) => {
  const { t } = useTranslation();

  const securityCultureStatus =
    hubspot?.securityCultureStatus ||
    (hubspot?.whiteGloveSecurityCulture ? 'Free trial' : undefined);

  const products = [
    {
      name: t('hubspot.productLifecycle.awareness'),
      status: hubspot?.awtStatus,
      onboardingDate: hubspot?.awtOnboardingDate,
    },
    {
      name: t('hubspot.productLifecycle.securityCulture'),
      status: securityCultureStatus,
      onboardingDate: hubspot?.securityCultureOnboardingDate,
    },
    {
      name: t('hubspot.productLifecycle.spearPhishing'),
      status: hubspot?.phishingStatus,
      onboardingDate: hubspot?.phishingOnboardingDate,
    },
    {
      name: t('hubspot.productLifecycle.autoPhishing'),
      status: hubspot?.autoPhishingStatus,
      onboardingDate: hubspot?.autoPhishingOnboardingDate,
    },
    {
      name: t('hubspot.productLifecycle.phishingOneOff'),
      status: hubspot?.phishingOneOffStatus,
      onboardingDate: hubspot?.phishingOneOffOnboardingDate,
    },
  ];

  return (
    <>
      <h3 className="mt-4 mb-2">{t('hubspot.productLifecycle')}</h3>
      <StyledSectionDescription>
        {t('hubspot.productLifecycle.description')}
      </StyledSectionDescription>
      <StyledCardsContainer>
        {products.map((product) => (
          <ProductLifecycleCard
            key={product.name}
            name={product.name}
            status={product.status}
            onboardingDate={product.onboardingDate}
          />
        ))}
      </StyledCardsContainer>
    </>
  );
};
